/**
 * Shared download-PDF affordance — icon-only, right-aligned on every
 * page that needs to export the current view as PDF.
 *
 * Visual contract (one job, one look, every page):
 *   - Download icon only, no text label.
 *   - Tooltip + aria-label carry the meaning ("Save as PDF" by default,
 *     callers can override via `title` for page-specific phrasing).
 *   - Same `Button` size + variant as the other ghost icon buttons in
 *     the dashboard chrome (Mail, Bell, etc.) so it sits flush.
 *
 * Behavior:
 *   - Click → fetches the report endpoint with credentials.
 *   - On 2xx → saves the PDF blob via a temporary `<a download>`.
 *   - On 4xx/5xx → surfaces the server's `message`/`error` inline so
 *     the user sees the precise reason (snapshot missing, render
 *     timeout, etc.) rather than a silent failure.
 *
 * The endpoint owns the actual generation; this is just the trigger.
 * Adding a new report = pointing this button at a new endpoint.
 */
import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui'

/**
 * Optional confirmation gate. When supplied, clicking the icon
 * opens a modal with the message before any fetch fires. The user
 * has to explicitly hit `confirmLabel` to start the download.
 *
 * Use this for endpoints that take a long time to generate or
 * produce a large artifact — the user should know what they're
 * committing to before they wait.
 */
export interface DownloadPdfConfirm {
  /** Dialog title — e.g. "Generate full PDF report?". */
  title: string
  /** Dialog body copy. Plain text; line breaks honored. Include
   * the expected wait time + any size warning here. */
  message: string
  /** Label on the confirm button. Defaults to "Generate". */
  confirmLabel?: string
}

export interface DownloadPdfButtonProps {
  /** The PDF endpoint URL — e.g.
   * `/api/reports/variant/${variantId}/pdf?window=30d&suite=…`. */
  url: string
  /** Tooltip + aria-label text. Defaults to "Save as PDF". Override for
   * page-specific phrasing (e.g. "Save run summary as PDF"). */
  title?: string
  /** Optional override for the suggested filename. When omitted, the
   * server's `Content-Disposition` filename is honored. */
  filename?: string
  /** Optional confirmation modal shown before the download starts.
   * Omit for one-click downloads (the existing behavior). */
  confirm?: DownloadPdfConfirm
}

export function DownloadPdfButton({
  url,
  title = 'Save as PDF',
  filename,
  confirm,
}: DownloadPdfButtonProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Confirm-dialog open state. Only relevant when `confirm` is
  // provided; without it the dialog is never mounted and the icon
  // click goes straight to the download.
  const [confirmOpen, setConfirmOpen] = useState(false)

  async function handleClick() {
    if (confirm && !confirmOpen) {
      // First click on a confirm-gated button: open the modal.
      // The actual download fires from the modal's confirm
      // button, which calls this same handler with confirmOpen
      // already true (we close it first below).
      setConfirmOpen(true)
      return
    }
    // Close the modal (if open) before starting the fetch so the
    // user sees the icon's spinner immediately rather than
    // hovering over a still-open dialog.
    if (confirm) setConfirmOpen(false)
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(url, { credentials: 'include' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(
          body?.message ||
            body?.error ||
            `Failed to download report (${res.status})`,
        )
      }
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename ?? filenameFromResponse(res) ?? 'report.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(blobUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  // Error surfaces inline to the LEFT of the icon so the icon stays
  // anchored on the right edge of its row regardless of error state.
  // Truncated text + a `title` attr lets the user hover for full
  // detail without the row growing. `role="alert"` so screen readers
  // are notified when a download fails — matches the alert-role pattern
  // the Pricing-page edit form uses for its inline save errors.
  return (
    <div className="flex items-center gap-2">
      {error && (
        <span
          role="alert"
          className="max-w-xs truncate text-xs text-destructive"
          title={error}
        >
          {error}
        </span>
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={handleClick}
        disabled={loading}
        title={loading ? 'Generating…' : title}
        aria-label={loading ? 'Generating PDF' : title}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
      </Button>
      {confirm && (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{confirm.title}</DialogTitle>
              <DialogDescription className="whitespace-pre-line">
                {confirm.message}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleClick}>
                {confirm.confirmLabel ?? 'Generate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function filenameFromResponse(res: Response): string | null {
  const cd = res.headers.get('content-disposition')
  if (!cd) return null
  const m = /filename="([^"]+)"/.exec(cd)
  return m?.[1] ?? null
}
